import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../i18n/I18nContext';
import { LogIn, RefreshCw, Lock } from 'lucide-react';
import Footer from '../components/Footer';
import Logo from '../components/Logo';
import '../styles/Auth.css';

const Login = () => {
  const navigate = useNavigate();
  const { login, resendVerification } = useAuth();
  const { t } = useI18n();

  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Rate-limit state
  const [rateLimitCountdown, setRateLimitCountdown] = useState(0);
  const rateLimitRef = useRef(null);

  // Email not verified state
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resending, setResending] = useState(false);
  const [resendError, setResendError] = useState('');
  const resendRef = useRef(null);

  useEffect(() => {
    return () => {
      if (rateLimitRef.current) clearInterval(rateLimitRef.current);
      if (resendRef.current) clearInterval(resendRef.current);
    };
  }, []);

  const startRateLimit = (seconds) => {
    setRateLimitCountdown(seconds);
    rateLimitRef.current = setInterval(() => {
      setRateLimitCountdown((s) => {
        if (s <= 1) { clearInterval(rateLimitRef.current); return 0; }
        return s - 1;
      });
    }, 1000);
  };

  const startResendCooldown = () => {
    setResendCooldown(60);
    resendRef.current = setInterval(() => {
      setResendCooldown((s) => {
        if (s <= 1) { clearInterval(resendRef.current); return 0; }
        return s - 1;
      });
    }, 1000);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
    setEmailNotVerified(false);
    setResendError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rateLimitCountdown > 0) return;
    setLoading(true);
    setError('');
    setEmailNotVerified(false);

    const result = await login(formData);

    if (result.success) {
      navigate('/dashboard');
    } else if (result.rateLimited) {
      startRateLimit(result.retryAfter ?? 60);
    } else if (result.emailNotVerified) {
      setEmailNotVerified(true);
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || resending) return;
    setResending(true);
    setResendError('');
    const result = await resendVerification(formData.email);
    setResending(false);
    if (result.success) {
      startResendCooldown();
    } else {
      setResendError(result.error || t('auth.login.resendFailed'));
    }
  };

  const isLocked = rateLimitCountdown > 0;

  return (
    <div className="page-shell">
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-logo">
            <Logo height={40} />
          </div>

          <h1>{t('auth.login.welcomeBack')}</h1>
          <p className="auth-subtitle">{t('auth.login.subtitle')}</p>

          {/* Rate limit banner */}
          {isLocked && (
            <div className="error-message auth-rate-limit">
              <Lock size={14} />
              <span>
                {t('auth.login.tooManyAttempts')}{' '}
                {t('auth.login.tryAgainIn', { seconds: rateLimitCountdown })}
              </span>
            </div>
          )}

          {/* Email not verified banner */}
          {emailNotVerified && !isLocked && (
            <div className="error-message auth-not-verified">
              <p>{t('auth.login.emailNotVerified')}</p>
              {resendError && <p style={{ color: '#f87171', fontSize: '0.82rem' }}>{resendError}</p>}
              <button
                className="auth-resend-btn"
                onClick={handleResend}
                disabled={resendCooldown > 0 || resending}
              >
                <RefreshCw size={13} />
                {resendCooldown > 0
                  ? t('auth.register.resendIn', { seconds: resendCooldown })
                  : t('auth.login.resendVerification')}
              </button>
            </div>
          )}

          {/* Generic error */}
          {error && !emailNotVerified && !isLocked && (
            <div className="error-message">{error}</div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="email">{t('auth.fields.email')}</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder={t('auth.fields.emailPlaceholder')}
                disabled={isLocked}
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">{t('auth.fields.password')}</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                placeholder={t('auth.fields.passwordPlaceholder')}
                disabled={isLocked}
              />
            </div>

            <p style={{ margin: '0 0 16px', textAlign: 'right' }}>
              <Link to="/forgot-password" style={{ fontSize: 13 }}>{t('auth.forgotPasswordLink')}</Link>
            </p>

            <button
              type="submit"
              className="btn-primary-auth"
              disabled={loading || isLocked}
            >
              {loading ? t('auth.login.signingIn') : isLocked ? (
                <><Lock size={16} /> {t('auth.login.tryAgainIn', { seconds: rateLimitCountdown })}</>
              ) : (
                <><LogIn size={20} /> {t('auth.login.signIn')}</>
              )}
            </button>
          </form>

          <p className="auth-footer">
            {t('auth.login.noAccount')} <Link to="/register">{t('auth.login.createOne')}</Link>
          </p>

          <Link to="/" className="back-home">
            {t('common.backToHome')}
          </Link>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Login;
